import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Facebook } from "lucide-react";

const SOCIALS = [
  {
    name: "Instagram",
    handle: "@seniorstylist.app",
    href: "https://instagram.com/seniorstylist.app",
    icon: Instagram,
    testid: "social-instagram",
  },
  {
    name: "YouTube",
    handle: "@myseniorstylist",
    href: "https://youtube.com/@myseniorstylist",
    icon: Youtube,
    testid: "social-youtube",
  },
  {
    name: "X (Twitter)",
    handle: "@myseniorstylist",
    href: "https://twitter.com/myseniorstylist",
    icon: XIcon,
    testid: "social-twitter",
  },
  {
    name: "TikTok",
    handle: "@seniorstylist.app",
    href: "https://tiktok.com/@seniorstylist.app",
    icon: TikTokIcon,
    testid: "social-tiktok",
  },
  {
    name: "Facebook",
    handle: "@myseniorstylist",
    href: "https://facebook.com/myseniorstylist",
    icon: Facebook,
    testid: "social-facebook",
  },
];

export const Footer = () => {
  return (
    <footer className="bg-stylist-ink text-white/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-14">
        <div className="grid md:grid-cols-3 gap-10">
          <div>
            <p className="font-serif text-2xl text-white">Senior Stylist</p>
            <p className="eyebrow text-stylist-rose mt-2">London · Est. 2026</p>
            <p className="mt-4 text-sm max-w-xs leading-relaxed">
              Your personal hair consultation subscription. Expert guidance, personalised to your
              unique style, all from your phone.
            </p>
            <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={s.testid}
                  aria-label={`${s.name} ${s.handle}`}
                  title={`${s.name} · ${s.handle}`}
                  className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-stylist-ink hover:bg-stylist-gold hover:border-stylist-gold transition-all duration-300"
                >
                  <s.icon className="w-[18px] h-[18px]" />
                </a>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-white/40 mb-4">Explore</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/how-it-works" className="hover:text-stylist-gold transition-colors">How It Works</Link></li>
              <li><a href="#journal" className="hover:text-stylist-gold transition-colors">Style Journal</a></li>
              <li><a href="#services" className="hover:text-stylist-gold transition-colors">Services</a></li>
              <li><a href="#why" className="hover:text-stylist-gold transition-colors">Why Us</a></li>
              <li><a href="#reviews" className="hover:text-stylist-gold transition-colors">Reviews</a></li>
              <li><a href="#faq" className="hover:text-stylist-gold transition-colors">FAQ</a></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-white/40 mb-4">Get in touch</p>
            <ul className="space-y-2 text-sm">
              <li><a href="mailto:support@senior-stylist.com" className="hover:text-stylist-gold transition-colors">support@senior-stylist.com</a></li>
              <li>London, United Kingdom</li>
            </ul>
            <div className="mt-5 flex flex-col gap-1.5 text-sm">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-stylist-gold transition-colors"
                >
                  <s.icon className="w-4 h-4" />
                  {s.handle}
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-xs text-white/40 font-mono">© 2026 Senior Stylist. All rights reserved.</span>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs" data-testid="footer-legal-links">
            <Link to="/legal/privacy" data-testid="footer-privacy-link" className="text-white/60 hover:text-stylist-gold transition-colors">Privacy Policy</Link>
            <Link to="/legal/cookies" data-testid="footer-cookies-link" className="text-white/60 hover:text-stylist-gold transition-colors">Cookies Policy</Link>
            <Link to="/legal/terms" data-testid="footer-terms-link" className="text-white/60 hover:text-stylist-gold transition-colors">Terms &amp; Conditions</Link>
            <button
              type="button"
              data-testid="footer-cookie-settings"
              onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
              className="text-white/60 hover:text-stylist-gold transition-colors"
            >
              Cookie settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

function XIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}
